import * as React from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as LocksState from "../../store/Locks";
import * as SitesState from "../../store/Sites";
import * as ClientUsersState from "../../store/ClientUsers";
import * as SiteUsersState from "../../store/SiteUsers";
import { confirmPrompt } from "../../helpers";
import { ConnectUserResponse } from "../../api/ApiClient";
import SelectClientUser from "../shared/SelectClientUser";

import Modal from "../shared/Modal";
import { useSnackbar } from "notistack";
import { Button } from "components/ui/button";

type OwnProps = {};

interface Injected {}
interface SnackProps {
  enqueueSnackbar: (node: React.ReactNode, options?: any) => void;
  closeSnackbar: (key?: any) => void;
}

type Props = Injected &
  typeof ClientUsersState.actionCreators &
  typeof SitesState.actionCreators &
  OwnProps &
  SnackProps;

type ModalType = "clientUser" | "backendUser" | null;
type Role = "user" | "admin";

interface State {
  inProgress: Object | null;

  selectedClientUser: ConnectUserResponse | null;
  showingModal: ModalType;
  //create clientUser
  name: string;
  email: string;
  role: Role;
  backendNameClaimId: string;
}

class AdminClientUsers extends React.Component<Props, State> {
  state: State = {
    inProgress: null,

    selectedClientUser: null,
    showingModal: null,
    name: "",
    email: "",
    role: "user",
    backendNameClaimId: "",
  };

  registerClientUser = (e: any, { isBackend } = { isBackend: false }) => {
    e.preventDefault();

    this.setState(
      {
        inProgress: {
          ...(this.state.inProgress || {}),
          registerClientUser: true,
        },
      },
      async () => {
        const state = this.state;
        try {
          const json = isBackend
            ? await this.props.registerBackendUser({
                backendNameClaimId: state.backendNameClaimId,
                name: state.name,
                email: state.email,
              })
            : ((await this.props.registerClientUser({
                name: state.name,
                email: state.email,
                role: state.role,
              })) as any);
          await this.props.requestClientUsers();
          this.props.enqueueSnackbar(
            <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
              Successfully registered user: {json?.name || json?.email}
            </p>,
            { variant: "success" },
          );
          this.closeModal();
          this.setState({
            //clear inputs
            name: "",
            email: "",
            role: "user",
            backendNameClaimId: "",
          });
        } catch (e) {
          let errorMessage = "Error registering user";
          try {
            const err: any = e;
            if (err?.response) {
              const data =
                typeof err.response === "string"
                  ? JSON.parse(err.response)
                  : err.response;
              if (data?.errors?.length) {
                errorMessage = data.errors
                  .map((x: any) => x.message)
                  .join(", ");
              } else if (data?.message) {
                errorMessage = data.message;
              }
            } else if (err?.message) {
              errorMessage = err.message;
            }
          } catch {
            // swallow parse errors
          }
          this.props.enqueueSnackbar(
            <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
              {errorMessage}
            </p>,
            { variant: "error" },
          );
        } finally {
          this.setState({
            inProgress: {
              ...(this.state.inProgress || {}),
              registerClientUser: false,
            },
          });
        }
      },
    );
  };

  deleteClientUser = (clientUser: ConnectUserResponse) => {
    confirmPrompt({
      promptMessage: "Are you sure you want to delete this clientUser?",
      onConfirm: async () => {
        try {
          await this.props.deleteClientUser({
            id: clientUser.id || "",
          });
          await this.props.requestSites();
        } catch (e) {
          let errorMessage = "Error deleting client user";
          try {
            const err: any = e;
            if (err?.response) {
              const data =
                typeof err.response === "string"
                  ? JSON.parse(err.response)
                  : err.response;
              if (data?.errors?.length) {
                errorMessage = data.errors
                  .map((x: any) => x.message)
                  .join(", ");
              } else if (data?.message) {
                errorMessage = data.message;
              }
            } else if (err?.message) {
              errorMessage = err.message;
            }
          } catch {
            // ignore parse issues
          }
          this.props.enqueueSnackbar(
            <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
              {errorMessage}
            </p>,
            { variant: "error" },
          );
        }
      },
    });
  };

  showAddClientUserModal = () => {
    this.setState({ showingModal: "clientUser" });
  };

  closeModal = () => {
    this.setState({ showingModal: null });
  };

  selectClientUser = (clientUser: ConnectUserResponse) => {
    const { selectedClientUser } = this.state;
    const wasSelected =
      selectedClientUser && selectedClientUser.id === clientUser.id;
    this.setState({
      selectedClientUser: wasSelected ? null : clientUser,
    });
  };

  public render() {
    const { showingModal } = this.state;

    return (
      <main className="space-y-6">
        <header className="pb-5 border-b border-slate-200 sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold leading-7 text-slate-900">
              Users
            </h1>
          </div>
          <div className="flex flex-wrap gap-2 mt-4 sm:mt-0">
            <Button
              data-testid="users-button-create"
              type="button"
              onClick={this.showAddClientUserModal}
            >
              Set Up User
            </Button>
            <Button
              data-testid="users-button-create-backend"
              type="button"
              onClick={() => this.setState({ showingModal: "backendUser" })}
            >
              Set Up Backend User
            </Button>
          </div>
        </header>
        {this.renderClientUsersTable()}

        <Modal
          show={!!showingModal}
          onHide={this.closeModal}
          aria-labelledby="ModalHeader"
        >
          <Modal.Header closeButton>
            <Modal.Title id="ModalHeader">
              {showingModal === "clientUser"
                ? "Add User"
                : showingModal === "backendUser"
                  ? "Add API User"
                  : "error"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {showingModal === "clientUser"
              ? this.renderRegisterClientUser()
              : showingModal === "backendUser"
                ? this.renderRegisterClientUser({ isBackend: true })
                : `error - unknown modal ${showingModal}`}
          </Modal.Body>
          <Modal.Footer>
            <Modal.Dismiss>Close</Modal.Dismiss>
          </Modal.Footer>
        </Modal>
      </main>
    );
  }

  private renderClientUserButton = (clientUser: ConnectUserResponse) => {
    return (
      <div>
        <Button
          type="button"
          variant="destructive"
          onClick={() => this.deleteClientUser(clientUser)}
        >
          Delete
        </Button>
        <p />
      </div>
    );
  };

  private renderClientUsersTable() {
    return (
      //@ts-ignore
      <SelectClientUser
        pageSize={5}
        renderButton={this.renderClientUserButton}
        unselectedLabel="Delete"
        unselectedBtnClass="btn-danger"
      />
    );
  }

  renderRegisterClientUser = ({ isBackend } = { isBackend: false }) => {
    const { inProgress, email, name, role, backendNameClaimId } = this.state;

    const { registerClientUser: registerClientUserInProgress = false } =
      (inProgress || {}) as any;

    return (
      <form>
        <div className="form-group">
          <label htmlFor="name">Name *</label>
          <input
            className="form-control"
            name="name"
            placeholder="User Name"
            value={name}
            onChange={(e) => this.setState({ name: e.target.value })}
          />
        </div>
        {!isBackend && (
          <React.Fragment>
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                className="form-control"
                name="email"
                placeholder="Email"
                value={email}
                onChange={(e) => this.setState({ email: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="role">Role (optional)</label>
              <select
                className="form-control"
                name="role"
                value={role}
                onChange={(e) =>
                  this.setState({ role: e.target.value as Role })
                }
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
              <p className="help-block">
                "user" can open locks, "admin" can create accounts, sites, locks
                and invite guests
              </p>
            </div>
          </React.Fragment>
        )}

        {isBackend && (
          <div className="form-group">
            <label htmlFor="backendNameClaimId">Backend Sub Claim</label>
            <input
              className="form-control"
              name="backendNameClaimId"
              placeholder="Backend Sub Claim"
              value={backendNameClaimId}
              onChange={(e) =>
                this.setState({ backendNameClaimId: e.target.value })
              }
            />
            <p className="help-block">
              The Auth0 "sub" claim for the API user, e.g
              MCv8D9MdWa71ToUoxO6A2MJkWvFqE5j0@clients
            </p>
          </div>
        )}

        <div>
          <Button
            type="submit"
            disabled={!!registerClientUserInProgress}
            onClick={(e) => this.registerClientUser(e, { isBackend })}
          >
            {isBackend ? "Register API User" : "Register User"}
          </Button>
        </div>
      </form>
    );
  };
}

const ConnectedAdminClientUsers = connect<any, any, any, ApplicationState>(
  (state) => ({
    clientUsers: state.clientUsers,
  }),
  {
    ...ClientUsersState.actionCreators,
    ...LocksState.actionCreators,
    ...SitesState.actionCreators,
    ...SiteUsersState.actionCreators,
  },
)(AdminClientUsers);

const AdminClientUsersWithSnackbar: React.FC<any> = (props) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  return (
    <ConnectedAdminClientUsers
      {...props}
      enqueueSnackbar={enqueueSnackbar}
      closeSnackbar={closeSnackbar}
    />
  );
};

export default AdminClientUsersWithSnackbar;
